import { type IUseGetSocialOfferIntegrationResponse, displayOrDownloadUrlDocument, useDownloadDocument } from "@api";
import type { ObjectId } from "@bbj-types/utils";
import { Checkbox, Divider, Stack, type Theme, Typography, useMediaQuery } from "@mui/material";
import { useGlobalContext } from "@root/context";
import { useI18nContext } from "@root/utils/organisation";
import { isAuthorize, useAuthorizer } from "@styleguide";
import clsx from "clsx";
import type { Dispatch, SetStateAction } from "react";
import { Link } from "react-router-dom";
import type { IPopoverState } from "../index";
import { IntegratedTableRow, classes } from "../styles";
import { Accompaniement } from "./accompaniement";
import { AuthorChip } from "./authorChip";

interface IPrescriptionCandidatesCardProps {
  data: IUseGetSocialOfferIntegrationResponse;
  socialApplySelected: ObjectId[];
  setSocialApplySelected: Dispatch<SetStateAction<ObjectId[]>>;
  setIntegratedPopoverState: Dispatch<SetStateAction<IPopoverState>>;
}

export const IntegratedCandidatesCard = ({
  data,
  socialApplySelected,
  setSocialApplySelected,
  setIntegratedPopoverState,
}: IPrescriptionCandidatesCardProps) => {
  const isDesktop = useMediaQuery((theme: Theme) => theme.breakpoints.up("desktop"));
  const { user } = useGlobalContext();
  const { timeZone } = useI18nContext();
  const hasVisualizeCandidatePermission = isAuthorize(user.roleV2, ["candidate.visualize"]);
  const isAuthorizedArchive = useAuthorizer(["offers.social.archive"]);
  const [, downloadDocument] = useDownloadDocument();

  const handleDownloadDocument = async () => {
    const res = await downloadDocument(data.prescription.documentId);
    if (res.status === 200) {
      displayOrDownloadUrlDocument(res.data, "display", data.prescription.name, "application/pdf");
    }
  };

  return (
    <IntegratedTableRow
      hasVisualizeCandidatePermission={hasVisualizeCandidatePermission}
      isAuthorizedArchive={isAuthorizedArchive}
      sx={{ flex: "auto" }}
    >
      <Stack className={classes.root} sx={{ textDecoration: "unset", color: "unset" }}>
        {isAuthorizedArchive && (
          <Checkbox
            size="small"
            color="primary"
            checked={socialApplySelected.includes(data._id)}
            onChange={(e) => {
              if (e.target.checked) {
                setSocialApplySelected((prev) => [...prev, data._id]);
              } else {
                setSocialApplySelected((prev) => prev.filter((id) => id !== data._id));
              }
            }}
          />
        )}
        {hasVisualizeCandidatePermission ? (
          <Stack
            className={clsx(classes.cell, classes.candidate)}
            component={Link}
            to={`/v2/candidate/${data.candidate._id}`}
            target="_blank"
          >
            <Typography
              variant="body1SemiBold"
              sx={{
                color: "primary.main",
                overflow: "hidden",
                textOverflow: "ellipsis",
                textDecoration: "underline",
              }}
            >
              {data.candidate.lastName} {data.candidate.firstName}
            </Typography>
          </Stack>
        ) : (
          <Stack className={clsx(classes.cell, classes.candidate)}>
            <Typography variant="body1SemiBold" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
              {data.candidate.lastName} {data.candidate.firstName}
            </Typography>
          </Stack>
        )}
        <Stack className={clsx(classes.cell, classes.prescription)}>
          {Object.keys(data.prescription).length > 0 ? (
            <Typography
              className={classes.document}
              variant="body1SemiBold"
              color="primary"
              onClick={handleDownloadDocument}
            >
              Fiche.pdf
            </Typography>
          ) : (
            <Typography
              variant="body1"
              sx={{
                color: "black.light",
                fontStyle: "italic",
                overflow: "hidden",
                textOverflow: "ellipsis",
              }}
            >
              Prescription manuelle
            </Typography>
          )}
        </Stack>

        <Stack className={clsx(classes.cell, classes.prescriptionDate)}>
          <Typography variant="body1" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {data.responseDate.toLocaleDateString("fr-FR", { timeZone })}
          </Typography>
        </Stack>
        <Stack className={clsx(classes.cell, classes.prescriptionDate)}>
          <Typography variant="body1" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {data.firstReceptionDate ? data.firstReceptionDate.toLocaleDateString("fr-FR", { timeZone }) : "-"}
          </Typography>
        </Stack>
        <Stack className={clsx(classes.cell, classes.prescriptionDate)}>
          <Typography variant="body1" sx={{ overflow: "hidden", textOverflow: "ellipsis" }}>
            {data.assessmentDate ? data.assessmentDate.toLocaleDateString("fr-FR", { timeZone }) : "-"}
          </Typography>
        </Stack>
        {isDesktop && (
          <Stack className={clsx(classes.cell, classes.author)}>
            <AuthorChip author={data.author} />
          </Stack>
        )}
        <Stack className={clsx(classes.cell, classes.accompaniement)}>
          <Accompaniement
            accompaniement={data.accompaniement}
            otherIntegratedSocialOffers={data.otherIntegratedSocialOffers}
            setAccompaniementPopoverState={setIntegratedPopoverState}
          />
        </Stack>
      </Stack>
      <Divider />
    </IntegratedTableRow>
  );
};
